/**
 * Zustand store for rulebook management
 * Handles enabling/disabling rulebooks and filtering content
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Rulebook, FilteredGameData, RulebookValidationResult } from '../types/rulebook';
import type { Character } from '../types/character';
import { rulebookService } from '../services/RulebookService';

interface RulebookState {
  // Current state
  availableRulebooks: Rulebook[];
  enabledRulebookIds: string[];
  filteredGameData: FilteredGameData | null;
  lastValidation: RulebookValidationResult | null;

  // UI state
  isLoading: boolean;
  hasUnsavedChanges: boolean;

  // Actions
  loadRulebooks: () => void;
  toggleRulebook: (rulebookId: string) => void;
  setEnabledRulebooks: (rulebookIds: string[]) => void;
  batchUpdateRulebooks: (updates: Array<{ rulebookId: string; enabled: boolean }>) => void;
  validate: (character: Character) => RulebookValidationResult;
  refreshFilteredData: () => void;
  resetToDefaults: () => void;
  ensureMinimumContent: () => boolean;
  getFallbackOptions: () => {
    hasMinimumContent: boolean;
    missingContentTypes: string[];
    suggestedRulebooks: string[];
  };
  isItemAvailable: (item: { rulebook: string }) => boolean;
  getUnavailableContent: (character: Character) => any;

  // Setting-based actions
  getAvailableSettings: () => any;
  setSettingEnabled: (settingId: string, enabled: boolean) => void;
  isSettingEnabled: (settingId: string) => boolean;
  isSettingPartiallyEnabled: (settingId: string) => boolean;
  getRulebooksBySettings: () => Record<string, Rulebook[]>;

  // Internal actions
  _setLoading: (loading: boolean) => void;
}

export const useRulebookStore = create<RulebookState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    availableRulebooks: [],
    enabledRulebookIds: [],
    filteredGameData: null,
    lastValidation: null,
    isLoading: false,
    hasUnsavedChanges: false,

    // Load available rulebooks and current settings
    loadRulebooks: () => {
      set({ isLoading: true });

      try {
        const availableRulebooks = rulebookService.getAvailableRulebooks();
        const enabledRulebookIds = rulebookService.getEnabledRulebookIds();
        const filteredGameData = rulebookService.getFilteredGameData();

        set({
          availableRulebooks,
          enabledRulebookIds,
          filteredGameData,
          isLoading: false,
          hasUnsavedChanges: false
        });
      } catch (error) {
        console.error('Failed to load rulebooks:', error);
        set({ isLoading: false });
      }
    },

    // Toggle a single rulebook on/off
    toggleRulebook: (rulebookId: string) => {
      const { enabledRulebookIds } = get();
      const isCurrentlyEnabled = enabledRulebookIds.includes(rulebookId);

      try {
        rulebookService.setRulebookEnabled(rulebookId, !isCurrentlyEnabled);

        // Update local state
        const newEnabledIds = rulebookService.getEnabledRulebookIds();
        const availableRulebooks = rulebookService.getAvailableRulebooks();
        const filteredGameData = rulebookService.getFilteredGameData();

        set({
          availableRulebooks,
          enabledRulebookIds: newEnabledIds,
          filteredGameData,
          hasUnsavedChanges: true
        });
      } catch (error) {
        console.error('Failed to toggle rulebook:', error);
      }
    },

    // Set multiple rulebooks enabled at once
    setEnabledRulebooks: (rulebookIds: string[]) => {
      try {
        rulebookService.setEnabledRulebooks(rulebookIds);

        // Update local state
        const availableRulebooks = rulebookService.getAvailableRulebooks();
        const enabledRulebookIds = rulebookService.getEnabledRulebookIds();
        const filteredGameData = rulebookService.getFilteredGameData();

        set({
          availableRulebooks,
          enabledRulebookIds,
          filteredGameData,
          hasUnsavedChanges: true
        });
      } catch (error) {
        console.error('Failed to set enabled rulebooks:', error);
      }
    },

    // Validate a character against current rulebook settings
    validate: (character: Character) => {
      const validation = rulebookService.validateCharacterRulebooks(character);

      set({ lastValidation: validation });

      return validation;
    },

    // Refresh filtered game data (useful after external changes)
    refreshFilteredData: () => {
      try {
        const filteredGameData = rulebookService.getFilteredGameData();
        set({ filteredGameData });
      } catch (error) {
        console.error('Failed to refresh filtered data:', error);
      }
    },

    // Batch update multiple rulebooks for better performance
    batchUpdateRulebooks: (updates: Array<{ rulebookId: string; enabled: boolean }>) => {
      try {
        const currentEnabled = get().enabledRulebookIds;
        let newEnabledIds = [...currentEnabled];

        updates.forEach(({ rulebookId, enabled }) => {
          // All rulebooks can now be disabled - no restrictions
          if (enabled && !newEnabledIds.includes(rulebookId)) {
            newEnabledIds.push(rulebookId);
          } else if (!enabled) {
            newEnabledIds = newEnabledIds.filter((id) => id !== rulebookId);
          }
        });

        // Apply all changes at once
        rulebookService.setEnabledRulebooks(newEnabledIds);

        // Update local state
        const availableRulebooks = rulebookService.getAvailableRulebooks();
        const enabledRulebookIds = rulebookService.getEnabledRulebookIds();
        const filteredGameData = rulebookService.getFilteredGameData();

        set({
          availableRulebooks,
          enabledRulebookIds,
          filteredGameData,
          hasUnsavedChanges: true
        });
      } catch (error) {
        console.error('Failed to batch update rulebooks:', error);
      }
    },

    // Reset to default settings (core only)
    resetToDefaults: () => {
      try {
        rulebookService.resetToDefaults();

        // Update local state
        const availableRulebooks = rulebookService.getAvailableRulebooks();
        const enabledRulebookIds = rulebookService.getEnabledRulebookIds();
        const filteredGameData = rulebookService.getFilteredGameData();

        set({
          availableRulebooks,
          enabledRulebookIds,
          filteredGameData,
          hasUnsavedChanges: true
        });
      } catch (error) {
        console.error('Failed to reset to defaults:', error);
      }
    },

    // Ensure minimum content is available for character creation
    ensureMinimumContent: () => {
      try {
        const wasChanged = rulebookService.ensureMinimumContent();

        if (wasChanged) {
          // Update local state after auto-enabling rulebooks
          const availableRulebooks = rulebookService.getAvailableRulebooks();
          const enabledRulebookIds = rulebookService.getEnabledRulebookIds();
          const filteredGameData = rulebookService.getFilteredGameData();

          set({
            availableRulebooks,
            enabledRulebookIds,
            filteredGameData,
            hasUnsavedChanges: true
          });
        }

        return wasChanged;
      } catch (error) {
        console.error('Failed to ensure minimum content:', error);
        return false;
      }
    },

    // Get fallback options when no content is available
    getFallbackOptions: () => {
      return rulebookService.getFallbackOptions();
    },

    // Check if an item is available based on current settings
    isItemAvailable: (item: { rulebook: string }) => {
      return rulebookService.isItemAvailable(item);
    },

    // Get unavailable content from a character
    getUnavailableContent: (character: Character) => {
      return rulebookService.getUnavailableCharacterContent(character);
    },

    // Setting-based actions
    getAvailableSettings: () => {
      return rulebookService.getAvailableSettings();
    },

    setSettingEnabled: (settingId: string, enabled: boolean) => {
      try {
        rulebookService.setSettingEnabled(settingId, enabled);

        // Update local state
        const availableRulebooks = rulebookService.getAvailableRulebooks();
        const enabledRulebookIds = rulebookService.getEnabledRulebookIds();
        const filteredGameData = rulebookService.getFilteredGameData();

        set({
          availableRulebooks,
          enabledRulebookIds,
          filteredGameData,
          hasUnsavedChanges: true
        });
      } catch (error) {
        console.error('Failed to set setting enabled:', error);
      }
    },

    isSettingEnabled: (settingId: string) => {
      return rulebookService.isSettingEnabled(settingId);
    },

    isSettingPartiallyEnabled: (settingId: string) => {
      return rulebookService.isSettingPartiallyEnabled(settingId);
    },

    getRulebooksBySettings: () => {
      return rulebookService.getRulebooksBySettings();
    },

    // Internal actions
    _setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    }
  }))
);

// Selectors for derived data
export const useRulebookSelectors = () => {
  const store = useRulebookStore();

  return {
    // Basic selectors
    enabledRulebooks: store.availableRulebooks.filter((r) => r.enabled),
    disabledRulebooks: store.availableRulebooks.filter((r) => !r.enabled),
    enabledCount: store.enabledRulebookIds.length,
    totalCount: store.availableRulebooks.length,

    // Content selectors with fallback handling
    availableCareers: store.filteredGameData?.careers || [],
    availableHomeworlds: store.filteredGameData?.homeworlds || [],
    availableBirthrights: store.filteredGameData?.birthrights || [],
    availableLures: store.filteredGameData?.lures || [],
    availableTrials: store.filteredGameData?.trials || [],
    availableMotivations: store.filteredGameData?.motivations || [],

    // Unavailable content selectors with fallback handling
    unavailableCareers: store.filteredGameData?.unavailableItems.careers || [],
    unavailableHomeworlds: store.filteredGameData?.unavailableItems.homeworlds || [],
    unavailableBirthrights: store.filteredGameData?.unavailableItems.birthrights || [],
    unavailableLures: store.filteredGameData?.unavailableItems.lures || [],
    unavailableTrials: store.filteredGameData?.unavailableItems.trials || [],
    unavailableMotivations: store.filteredGameData?.unavailableItems.motivations || [],

    // Validation selectors
    hasValidationErrors: store.lastValidation?.errors.length > 0,
    hasValidationWarnings: store.lastValidation?.warnings.length > 0,
    validationErrors: store.lastValidation?.errors || [],
    validationWarnings: store.lastValidation?.warnings || [],
    missingRulebooks: store.lastValidation?.missingRulebooks || [],

    // Utility functions
    getRulebookById: (id: string) => store.availableRulebooks.find((r) => r.id === id),
    isRulebookEnabled: (id: string) => store.enabledRulebookIds.includes(id),
    canDisableRulebook: (id: string) => id !== 'core', // Core cannot be disabled

    // Advanced filtering utilities
    getFilteredItems: <T extends { rulebook: string }>(
      items: T[],
      includeUnavailable: boolean = false
    ): T[] => {
      if (includeUnavailable) return items;
      return items.filter((item) => store.enabledRulebookIds.includes(item.rulebook));
    },

    getItemsByRulebook: <T extends { rulebook: string }>(items: T[], rulebookId: string): T[] => {
      return items.filter((item) => item.rulebook === rulebookId);
    },

    hasAvailableContent: (contentType: string) => {
      if (!store.filteredGameData) return false;
      const content = (store.filteredGameData as unknown)[contentType];
      return Array.isArray(content) && content.length > 0;
    },

    getContentStats: () => {
      if (!store.filteredGameData) return null;

      return {
        available: {
          careers: store.filteredGameData.careers.length,
          homeworlds: store.filteredGameData.homeworlds.length,
          birthrights: store.filteredGameData.birthrights.length,
          lures: store.filteredGameData.lures.length,
          trials: store.filteredGameData.trials.length,
          motivations: store.filteredGameData.motivations.length
        },
        unavailable: {
          careers: store.filteredGameData.unavailableItems.careers.length,
          homeworlds: store.filteredGameData.unavailableItems.homeworlds.length,
          birthrights: store.filteredGameData.unavailableItems.birthrights.length,
          lures: store.filteredGameData.unavailableItems.lures.length,
          trials: store.filteredGameData.unavailableItems.trials.length,
          motivations: store.filteredGameData.unavailableItems.motivations.length
        }
      };
    },

    // Setting-based selectors
    availableSettings: store.getAvailableSettings(),
    rulebooksBySettings: store.getRulebooksBySettings(),

    // Setting utility functions
    isSettingEnabled: (settingId: string) => store.isSettingEnabled(settingId),
    isSettingPartiallyEnabled: (settingId: string) => store.isSettingPartiallyEnabled(settingId),

    getSettingStatus: (settingId: string) => {
      const isEnabled = store.isSettingEnabled(settingId);
      const isPartial = store.isSettingPartiallyEnabled(settingId);

      if (isEnabled) return 'enabled';
      if (isPartial) return 'partial';
      return 'disabled';
    }
  };
};
